import { useState } from "react";
import { PATHS, SERVICE_COPY, ASSETS } from "@/lib/content";
import { Reveal, Overline } from "@/components/site/Reveal";
import PayButton from "@/components/site/PayButton";
import LeadDialog from "@/components/site/LeadDialog";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowUpRight, BookOpen, MousePointerClick } from "lucide-react";

const PRICES = {
  strategy_session: "$300",
  ongoing_partnership: "$2,500",
  reading_intervention: "$1,500",
  homeschool_strategy: "$300",
  evaluation_assessment: "Contact for pricing",
};

const CTA_LABELS = {
  strategy_session: "Book Session",
  ongoing_partnership: "Begin Partnership",
  reading_intervention: "Begin Program",
  homeschool_strategy: "Book Sessions",
};

function ServiceCard({ sid, pathId }) {
  const s = SERVICE_COPY[sid];
  const isEval = sid === "evaluation_assessment";
  const displayName =
    pathId === "families" && sid === "strategy_session"
      ? "Family Strategy Session"
      : s.name;
  return (
    <div
      data-testid={`service-${sid}`}
      className="flex flex-col rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(44,42,38,0.15)]"
    >
      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {s.duration}
      </span>
      <h4 className="mt-3 font-serif text-3xl font-light leading-tight">{displayName}</h4>
      <p className="mt-2 text-muted-foreground">{s.tagline}</p>
      <p className={`mt-6 font-serif text-primary ${isEval ? "text-2xl" : "text-4xl"}`}>
        {PRICES[sid]}
      </p>

      {s.features ? (
        <ul className="mt-6 space-y-3 flex-1">
          {s.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-foreground/80">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
              {f}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-6 space-y-4 flex-1 text-sm leading-relaxed text-muted-foreground">
          {s.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      )}

      <div className="mt-8">
        {isEval ? (
          <LeadDialog
            leadType="evaluation"
            path="families"
            overline="Evaluation & Assessment"
            title="Request evaluation & assessment info"
            description="Tell us about your child and goals. Assessment fees are provided directly by the evaluating practice after an initial consultation."
            withMessage
            messageLabel="About your child"
            testId="evaluation-inquiry"
            trigger={
              <button
                data-testid={`pay-${sid}-${pathId}`}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-foreground/25 px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium transition-all duration-300 hover:border-primary hover:text-primary hover:-translate-y-1"
              >
                Contact for Pricing
                <ArrowUpRight
                  className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={1.5}
                />
              </button>
            }
          />
        ) : (
          <PayButton
            packageId={sid}
            path={pathId}
            testId={`pay-${sid}-${pathId}`}
            className="w-full"
          >
            {CTA_LABELS[sid]}
          </PayButton>
        )}
      </div>
    </div>
  );
}

function StepUpBadge() {
  return (
    <a
      href="https://www.stepupforstudents.org/schools-and-providers/"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="stepup-badge"
      className="group flex flex-col sm:flex-row sm:items-center gap-5 rounded-2xl border border-border bg-muted/40 px-6 py-5 transition-colors duration-300 hover:border-primary"
    >
      <img
        src={ASSETS.stepUpLogo}
        alt="Step Up For Students — EMA Marketplace Provider"
        className="h-12 w-auto shrink-0"
      />
      <span className="text-sm leading-relaxed text-foreground/80 max-w-xl">
        We are an <strong className="font-medium text-foreground">approved Step Up For Students</strong> provider.
        Families may apply eligible EMA / Step Up scholarship funds toward our services.
      </span>
      <ArrowUpRight
        className="hidden sm:block ml-auto h-5 w-5 text-primary transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        strokeWidth={1.5}
      />
    </a>
  );
}

function StandardLayout({ path }) {
  return (
    <div className="grid lg:grid-cols-12 gap-10 items-stretch">
      <div className="lg:col-span-5 flex flex-col">
        <div className="overflow-hidden rounded-2xl mb-8">
          <img
            src={ASSETS[path.image]}
            alt=""
            className="w-full object-cover aspect-[5/4]"
            style={{ objectPosition: path.objectPosition || "center" }}
          />
        </div>
        <Overline>{path.kicker}</Overline>
        <h3 className="mt-4 font-serif text-3xl md:text-4xl font-light leading-tight">
          {path.title}
        </h3>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{path.body}</p>
        {path.funnel && (
          <a
            href={path.funnel.href}
            data-testid={`path-funnel-${path.id}`}
            className="group mt-8 inline-flex items-center gap-2 text-primary text-sm uppercase tracking-[0.16em]"
          >
            {path.funnel.name}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
          </a>
        )}
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
        {path.services.map((sid) => (
          <ServiceCard key={sid} sid={sid} pathId={path.id} />
        ))}
      </div>
    </div>
  );
}

function FamiliesLayout({ path }) {
  return (
    <div className="space-y-10">
      <StepUpBadge />

      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 overflow-hidden rounded-2xl">
          <img src={ASSETS.familyHighFive} alt="" className="w-full object-cover aspect-[4/5]" />
        </div>
        <div className="lg:col-span-7">
          <Overline>{path.kicker}</Overline>
          <h3 className="mt-4 font-serif text-3xl md:text-4xl font-light leading-tight">
            {path.title}
          </h3>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{path.body}</p>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
            {path.funnel && (
              <a
                href={path.funnel.href}
                data-testid={`path-funnel-${path.id}`}
                className="group inline-flex items-center gap-2 text-primary text-sm uppercase tracking-[0.16em]"
              >
                {path.funnel.name}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </a>
            )}
            <LeadDialog
              leadType="reading_support"
              path="families"
              overline="For Families"
              title="Request reading support information"
              description="Share a little about your child and your current reading concerns."
              withMessage
              messageLabel="About your child"
              testId="reading-support"
              trigger={
                <button
                  data-testid="reading-support-trigger"
                  className="group inline-flex items-center gap-2 text-foreground/70 text-sm uppercase tracking-[0.16em] hover:text-primary transition-colors"
                >
                  <BookOpen className="h-4 w-4" strokeWidth={1.5} />
                  Reading Support Info
                </button>
              }
            />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        {path.services.map((sid) => (
          <ServiceCard key={sid} sid={sid} pathId={path.id} />
        ))}
      </div>
    </div>
  );
}

export default function ChoosePath() {
  const [active, setActive] = useState("professionals");
  const path = PATHS.find((p) => p.id === active);

  return (
    <section id="paths" data-testid="choose-path" className="relative py-24 md:py-40 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <Overline>My Services</Overline>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight">
              Where are you leading from?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Whether you're leading an organization, building a business, and/or
              raising a family, every environment shapes the people within it. The
              principles are consistent — the application is personal. Choose the
              path that fits your season, and we'll design the next step together.
            </p>
          </Reveal>
        </div>

        {/* Path selector */}
        <div className="mt-12">
          <p className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-primary">
            <MousePointerClick className="h-4 w-4" strokeWidth={1.5} />
            Select your path
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {PATHS.map((p, i) => {
              const isActive = active === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`path-tab-${p.id}`}
                  onClick={() => setActive(p.id)}
                  className={`group relative overflow-hidden rounded-2xl border p-6 text-left transition-colors duration-300 ${
                    isActive ? "border-foreground" : "border-border hover:border-primary"
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activePathBg"
                      className="absolute inset-0 bg-foreground"
                      transition={{ type: "spring", stiffness: 420, damping: 40 }}
                    />
                  )}
                  <div className="relative z-10 flex items-start justify-between gap-3">
                    <div>
                      <span className={`font-serif text-sm ${isActive ? "text-background/50" : "text-primary"}`}>
                        0{i + 1}
                      </span>
                      <p className={`mt-2 font-serif text-xl leading-tight ${isActive ? "text-background" : "text-foreground"}`}>
                        {p.tabLabel}
                      </p>
                      <p className={`mt-1 text-xs ${isActive ? "text-background/60" : "text-muted-foreground"}`}>
                        {p.tabSub}
                      </p>
                    </div>
                    <ArrowRight
                      className={`h-4 w-4 shrink-0 mt-1 transition-all duration-300 ${
                        isActive
                          ? "text-background translate-x-0 opacity-100"
                          : "text-primary -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"
                      }`}
                      strokeWidth={1.5}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12"
          >
            {active === "families" ? (
              <FamiliesLayout path={path} />
            ) : (
              <StandardLayout path={path} />
            )}
          </motion.div>
        </AnimatePresence>

        <Reveal>
          <div
            data-testid="services-questions-band"
            className="mt-20 rounded-3xl border border-border bg-muted/50 px-8 py-12 md:px-14 md:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div>
              <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight">
                Not ready to get started, but have questions?
              </h3>
              <p className="mt-3 max-w-xl text-lg text-muted-foreground leading-relaxed">
                Let's talk it through. Share where you are and what you're weighing —
                I'll help you find the right next step, no pressure.
              </p>
            </div>
            <LeadDialog
              leadType="general"
              overline="No pressure"
              title="Ask a question"
              description="Tell me a little about what you're navigating and I'll personally follow up."
              withMessage
              messageLabel="Your question"
              testId="services-questions"
              trigger={
                <button
                  data-testid="services-questions-trigger"
                  className="group shrink-0 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-primary-foreground transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(193,119,103,0.6)]"
                >
                  Ask a Question
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </button>
              }
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
